import { WORK_ORDER_TYPES, type WorkOrderType } from "@bt/domain";
import {
  CLASSIC_YEARS,
  type InstallationReport,
  MILEAGE_EDGES_KM,
  VEHICLE_AGE_EDGES,
  YOUNGTIMER_YEARS,
} from "@bt/domain/report";
import { useTranslation } from "react-i18next";
import { useAdminFormat } from "../format";
import {
  BucketsFigure,
  CountsFigure,
  DistributionFigure,
  Grid,
  ReportView,
  Section,
  TallyFigure,
} from "./kit";

/** Operating systems and browsers by their product names, which aren't translated. */
const PRODUCT_NAMES: Record<string, string> = {
  windows: "Windows",
  macos: "macOS",
  ios: "iOS",
  android: "Android",
  linux: "Linux",
  chromeos: "ChromeOS",
  chrome: "Chrome",
  safari: "Safari",
  firefox: "Firefox",
  edge: "Edge",
  samsung: "Samsung Internet",
  opera: "Opera",
};

/** Who the customers are (docs/admin-app/figures.md › What the owner wants to know). */
export function CustomersPage() {
  const { t } = useTranslation();
  const product = (v: string) => PRODUCT_NAMES[v] ?? (v === "other" ? t("adminApp.kit.other") : v);
  return (
    <div className="space-y-8">
      <Section
        title={t("adminApp.customers.vehicles")}
        description={t("adminApp.customers.vehiclesHint")}
      >
        <ReportView>{(report) => <Vehicles report={report} />}</ReportView>
      </Section>
      <Section
        title={t("adminApp.customers.people")}
        description={t("adminApp.customers.peopleHint")}
      >
        <ReportView>{(report) => <People report={report} />}</ReportView>
        <Grid>
          <CountsFigure
            title={t("adminApp.customers.regions")}
            source="usage"
            name="locale.region"
            csvName="regions"
          />
          <CountsFigure
            title={t("adminApp.customers.deviceTypes")}
            source="usage"
            name="device.type"
            csvName="device-types"
            labelOf={(v) =>
              ["phone", "tablet", "desktop", "unknown"].includes(v)
                ? t(`adminApp.devices.${v as "phone"}`)
                : v
            }
          />
          <CountsFigure
            title={t("adminApp.customers.os")}
            source="usage"
            name="device.os"
            csvName="device-os"
            labelOf={product}
          />
          <CountsFigure
            title={t("adminApp.customers.browsers")}
            source="usage"
            name="device.browser"
            csvName="device-browsers"
            labelOf={product}
          />
        </Grid>
      </Section>
      <Section title={t("adminApp.customers.work")} description={t("adminApp.customers.workHint")}>
        <ReportView>{(report) => <Work report={report} />}</ReportView>
      </Section>
    </div>
  );
}

function Vehicles({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const vehicles = report.vehicles;
  return (
    <div className="space-y-4">
      <Grid>
        <TallyFigure
          title={t("adminApp.customers.segments")}
          description={t("adminApp.customers.segmentsHint")}
          rows={report.segments?.garages}
          csvName="segments"
          labelOf={(v) => t(`adminApp.segments.${v as "one"}`)}
          base={
            report.garages?.total !== undefined
              ? report.garages.total - (report.garages.statsExcluded ?? 0)
              : undefined
          }
        />
        <DistributionFigure
          title={t("adminApp.customers.vehiclesPerGarage")}
          perGarage={vehicles?.perGarage}
          csvName="vehicles-per-garage"
        />
        <TallyFigure
          title={t("adminApp.customers.kinds")}
          description={t("adminApp.customers.kindsHint")}
          rows={vehicles?.kinds}
          csvName="vehicle-kinds"
          labelOf={(v) => t(`adminApp.kinds.${v as "car"}`)}
          base={vehicles?.total}
        />
        <TallyFigure
          title={t("adminApp.customers.makes")}
          rows={vehicles?.makes}
          csvName="makes"
          limit={10}
          base={vehicles?.total}
        />
        <TallyFigure
          title={t("adminApp.customers.chassisCodes")}
          description={t("adminApp.customers.chassisCodesHint")}
          rows={report.chassisCodes?.codes?.map((c) => ({ value: c.key, count: c.vehicles ?? 0 }))}
          csvName="chassis-codes"
          limit={12}
        />
        <TallyFigure
          title={t("adminApp.customers.makePackages")}
          rows={report.chassisCodes?.makePackages}
          csvName="make-packages"
        />
        <TallyFigure
          title={t("adminApp.customers.models")}
          description={t("adminApp.customers.sharedHint")}
          rows={vehicles?.models}
          csvName="models"
          limit={10}
        />
        <TallyFigure
          title={t("adminApp.customers.engines")}
          description={t("adminApp.customers.sharedHint")}
          rows={vehicles?.engineCodes}
          csvName="engine-codes"
          limit={10}
        />
        <BucketsFigure
          title={t("adminApp.customers.age")}
          description={t("adminApp.customers.ageHint", {
            youngtimer: YOUNGTIMER_YEARS,
            classic: CLASSIC_YEARS,
          })}
          counts={vehicles?.ageBuckets}
          edges={VEHICLE_AGE_EDGES}
          formatEdge={String}
          unknown={vehicles?.ageUnknown}
          csvName="vehicle-age"
        />
        <BucketsFigure
          title={t("adminApp.customers.mileage")}
          counts={vehicles?.mileageBuckets}
          edges={MILEAGE_EDGES_KM}
          formatEdge={format.compact}
          continuous
          unknown={vehicles?.mileageUnknown}
          csvName="mileage"
        />
      </Grid>
    </div>
  );
}

function People({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const settings = report.garageSettings;
  const language = (v: string) =>
    ["en", "de", "device"].includes(v) ? t(`adminApp.languages.${v as "en"}`) : v;
  return (
    <Grid columns={3}>
      <TallyFigure
        title={t("adminApp.customers.languages")}
        description={t("adminApp.customers.languagesHint")}
        rows={report.users?.languages}
        csvName="languages"
        labelOf={language}
      />
      <TallyFigure
        title={t("adminApp.customers.appearance")}
        rows={report.users?.themes}
        csvName="appearance"
        labelOf={(v) =>
          ["light", "dark", "system", "device"].includes(v)
            ? t(`adminApp.themes.${v as "light"}`)
            : v
        }
      />
      <TallyFigure
        title={t("adminApp.customers.garageLanguages")}
        rows={settings?.languages}
        csvName="garage-languages"
        labelOf={language}
      />
      <TallyFigure
        title={t("adminApp.customers.currencies")}
        rows={settings?.currencies}
        csvName="currencies"
      />
      <TallyFigure
        title={t("adminApp.customers.taxRates")}
        rows={settings?.baseTaxRates}
        csvName="tax-rates"
        labelOf={(v) => format.percent(Number(v) / 10_000)}
      />
      <TallyFigure
        title={t("adminApp.customers.mileageUnits")}
        rows={settings?.mileageUnits}
        csvName="mileage-units"
      />
      <TallyFigure
        title={t("adminApp.customers.priceBases")}
        rows={settings?.priceBases}
        csvName="price-bases"
        labelOf={(v) =>
          v === "incl_tax" || v === "excl_tax" ? t(`adminApp.customers.priceBase.${v}`) : v
        }
      />
    </Grid>
  );
}

function Work({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const work = report.workDone;
  return (
    <Grid columns={3}>
      <TallyFigure
        title={t("adminApp.customers.workOrderTypes")}
        rows={work?.workOrderTypes}
        csvName="work-order-types"
        labelOf={(v) =>
          (WORK_ORDER_TYPES as readonly string[]).includes(v)
            ? t(`workOrderType.${v as WorkOrderType}`)
            : v
        }
      />
      <TallyFigure
        title={t("adminApp.customers.mainGroups")}
        description={t("adminApp.customers.mainGroupsHint")}
        rows={work?.mainGroups}
        csvName="main-groups"
        limit={12}
      />
      <TallyFigure
        title={t("adminApp.customers.tags")}
        description={t("adminApp.customers.sharedHint")}
        rows={work?.tags}
        csvName="tags"
        limit={12}
      />
    </Grid>
  );
}
